/**
 * Header
 * v2 — 5+1 navigation driven by react-router's `useLocation()`.
 *
 * - Primary (5)   : Home, About, Experience, Work, Blog
 * - More (1 + 2)  : kevin-bot, Contact (rendered in dropdown; Now/Talks hidden)
 * - Controls (3)  : Theme, Sound, Settings (40×40, surface + border, radius-sm)
 * - Mobile        : LogoMark + Hamburger → MobileDrawer
 *
 * Active state is derived from the route, NOT from IntersectionObserver.
 * Visuals match `design/home.pen` § Header (mono LogoMark, accent-bg active
 * pill, elevated bar with subtle bottom border).
 */
import * as React from "react";
import { NavLink, useLocation } from "react-router";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { ChevronDown, Menu, X, Activity, Mic, Sparkles, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NavItem } from "../ui/molecules/NavItem";
import { LogoMark } from "../ui/molecules/LogoMark";
import { SoundToggle } from "../ui/molecules/SoundToggle";
import { ThemeToggle } from "../ui/molecules/ThemeToggle";
import { SettingsMenu } from "../ui/molecules/SettingsMenu";
import { MobileDrawer } from "../ui/molecules/MobileDrawer";
import { PRIMARY_NAV, SECONDARY_NAV, getActiveNav } from "../config/navigation";
import { useNeuralAudio } from "../lib/audio";

const SECONDARY_ICONS: Record<
  string,
  React.ComponentType<{ size?: number; className?: string }>
> = {
  now: Activity,
  talks: Mic,
  kevinBot: Sparkles,
  contact: Mail,
};

const Header: React.FC = () => {
  const { t } = useTranslation();
  const { play } = useNeuralAudio();
  const { pathname } = useLocation();
  const { scrollY } = useScroll();
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isMoreOpen, setIsMoreOpen] = React.useState(false);
  const moreRef = React.useRef<HTMLDivElement | null>(null);

  useMotionValueEvent(scrollY, "change", (latest) => {
    setIsScrolled(latest > 50);
  });

  const activeKey = getActiveNav(pathname);
  const isMoreActive = activeKey === "More";

  // Click outside / Escape closes the More dropdown
  React.useEffect(() => {
    if (!isMoreOpen) return;
    const handleClick = (event: MouseEvent) => {
      if (!moreRef.current?.contains(event.target as Node)) {
        setIsMoreOpen(false);
      }
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsMoreOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [isMoreOpen]);

  // Close dropdown on route change
  React.useEffect(() => {
    setIsMoreOpen(false);
  }, [pathname]);

  const handleLogoClick = () => {
    play("beep");
  };

  const toggleMobileMenu = () => {
    play("system");
    setIsMobileMenuOpen((v) => !v);
  };

  const toggleMore = () => {
    play("click");
    setIsMoreOpen((v) => !v);
  };

  return (
    <>
      <a
        href="#main-content"
        className="fixed left-4 top-4 z-[200] -translate-y-[150%] rounded-md bg-accent px-4 py-2 text-sm font-semibold text-accent-fg transition-transform focus-visible:translate-y-0"
      >
        Skip to content
      </a>
      <header
        className={`fixed top-0 left-0 right-0 z-[150] bg-surface-elevated border-b border-border-subtle transition-all duration-500 ${
          isScrolled ? "py-3 shadow-lg" : "py-5"
        }`}
      >
        <div className="mx-auto max-w-[1920px] px-6 lg:px-12 flex items-center justify-between">
          {/* Left: Logo */}
          <LogoMark size="md" onClick={handleLogoClick} />

          {/* Center: Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {PRIMARY_NAV.map((link) => {
              const isActive = activeKey === link.id;
              return (
                <NavItem
                  key={link.id}
                  href={link.href}
                  active={isActive}
                  onClick={() => {
                    play("click");
                    setIsMoreOpen(false);
                  }}
                  onMouseEnter={() => play("hover")}
                >
                  {t(link.labelKey, { defaultValue: link.id })}
                </NavItem>
              );
            })}

            {/* More ▾ dropdown */}
            <div ref={moreRef} className="relative">
              <button
                type="button"
                onClick={toggleMore}
                aria-haspopup="menu"
                aria-expanded={isMoreOpen}
                onMouseEnter={() => play("hover")}
                className={`px-4 py-2.5 rounded-sm text-sm flex items-center gap-1 font-medium outline-none cursor-pointer transition-colors duration-300 ${
                  isMoreActive
                    ? "bg-accent-bg text-accent font-semibold"
                    : "text-secondary hover:text-primary"
                }`}
              >
                {t("nav.more", { defaultValue: "More" })}
                <ChevronDown
                  size={14}
                  className={`text-tertiary transition-transform ${isMoreOpen ? "rotate-180" : ""}`}
                />
              </button>

              <AnimatePresence>
                {isMoreOpen && (
                  <motion.div
                    key="more-menu"
                    role="menu"
                    initial={{ opacity: 0, y: -4, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -4, scale: 0.98 }}
                    transition={{ duration: 0.12 }}
                    className="absolute right-0 top-full mt-2 w-60 glass-panel rounded-md p-2 shadow-2xl z-50"
                  >
                    {SECONDARY_NAV.map(({ id, href, labelKey }) => {
                      const Icon = SECONDARY_ICONS[id];
                      const isCurrent = pathname.startsWith(href);
                      return (
                        <NavLink
                          key={id}
                          to={href}
                          role="menuitem"
                          onClick={() => {
                            play("click");
                            setIsMoreOpen(false);
                          }}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-sm hover:bg-surface-elevated text-sm transition-colors ${
                            isCurrent ? "text-accent" : "text-primary"
                          }`}
                        >
                          {Icon ? <Icon size={16} className="text-secondary" /> : null}
                          {t(labelKey, { defaultValue: id })}
                        </NavLink>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </nav>

          {/* Right: Controls (Desktop) — Theme, Sound, Settings per design */}
          <div className="hidden md:flex items-center gap-2">
            <ThemeToggle />
            <SoundToggle />
            <SettingsMenu />
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-2">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={toggleMobileMenu}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              className="p-3 rounded-md bg-surface border border-border text-primary"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </motion.button>
          </div>
        </div>
      </header>

      <MobileDrawer isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
    </>
  );
};

export default Header;
